import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Gift, Coins, Trophy, RotateCcw, Volume2, VolumeX, ShieldCheck, Check, ShoppingBag, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Username } from "../UserBadge";

const RARITY_THEMES = {
  "mil-spec": { label: "Mil-Spec", color: "#4b69ff", bg: "rgba(75, 105, 255, 0.15)", border: "#4b69ff80", text: "text-blue-400" },
  "restricted": { label: "Restricted", color: "#8847ff", bg: "rgba(136, 71, 255, 0.15)", border: "#8847ff80", text: "text-purple-400" },
  "classified": { label: "Classified", color: "#d32ce6", bg: "rgba(211, 44, 230, 0.15)", border: "#d32ce680", text: "text-pink-400" },
  "covert": { label: "Covert", color: "#eb4b4b", bg: "rgba(235, 75, 75, 0.15)", border: "#eb4b4b80", text: "text-red-400" },
  "extraordinary": { label: "★ Special Rare", color: "#ffd700", bg: "rgba(255, 215, 0, 0.2)", border: "#ffd700cc", text: "text-amber-300" },
};

const CARD_WIDTH = 180;
const CARD_GAP = 12;
const SLOT_SIZE = CARD_WIDTH + CARD_GAP;
const WINNING_SLOT = 43;

export default function CaseOpeningLab({ user }) {
  const queryClient = useQueryClient();
  const [selectedCase, setSelectedCase] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [reel, setReel] = useState([]);
  const [winningSkin, setWinningSkin] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [activeTab, setActiveTab] = useState("cases"); // 'cases' | 'inventory'
  const reelRef = useRef(null);

  const { data: casesList = [] } = useQuery({
    queryKey: ["arenaCases"],
    queryFn: async () => (await api.get("/arena/cases")).data,
    staleTime: 60000
  });

  const { data: inventory = [], refetch: refetchInventory } = useQuery({
    queryKey: ["arenaInventory"],
    queryFn: async () => (await api.get("/arena/inventory")).data,
    enabled: !!user,
    staleTime: 10000
  });

  useEffect(() => {
    if (casesList.length > 0 && !selectedCase) {
      setSelectedCase(casesList[0]);
    }
  }, [casesList, selectedCase]);

  // Open Case Mutation
  const openCaseMutation = useMutation({
    mutationFn: async (caseId) => (await api.post("/arena/cases/open", { case_id: caseId })).data,
    onSuccess: (data) => {
      setReel(data.reel);
      setWinningSkin(data.winning_skin);
      setIsSpinning(true);
      setShowCelebration(false);

      // Trigger spin physics animation
      setTimeout(() => {
        if (reelRef.current) {
          // Calculate offset to land winning slot 43 in center
          const containerWidth = reelRef.current.parentElement.offsetWidth;
          const targetOffset = (WINNING_SLOT * SLOT_SIZE) - (containerWidth / 2) + (CARD_WIDTH / 2) + (Math.random() * 40 - 20);
          
          reelRef.current.style.transition = "transform 5.5s cubic-bezier(0.12, 0.85, 0.15, 1)";
          reelRef.current.style.transform = `translateX(-${targetOffset}px)`;
        }
      }, 50);

      // Finish spin after 5.8s
      setTimeout(() => {
        setIsSpinning(false);
        setShowCelebration(true);
        refetchInventory();
        queryClient.invalidateQueries(["userProfile"]);
        toast.success(`Ai deblocat: ${data.winning_skin.weapon} | ${data.winning_skin.name}!`);
      }, 5800);
    },
    onError: (err) => {
      setIsSpinning(false);
      toast.error(err?.response?.data?.detail || "Eroare la deschiderea cutiei.");
    }
  });

  // Sell Skin Mutation
  const sellSkinMutation = useMutation({
    mutationFn: async (itemId) => (await api.post("/arena/inventory/sell", { item_id: itemId })).data,
    onSuccess: (data) => {
      toast.success(data.message);
      refetchInventory();
      queryClient.invalidateQueries(["userProfile"]);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Eroare la vânzarea skin-ului.");
    }
  });

  const handleStartOpen = () => {
    if (!user) {
      toast.error("Trebuie să fii autentificat pentru a deschide cutii!");
      return;
    }
    if (!selectedCase || isSpinning) return;

    // Reset reel position before new spin
    if (reelRef.current) {
      reelRef.current.style.transition = "none";
      reelRef.current.style.transform = "translateX(0px)";
    }
    openCaseMutation.mutate(selectedCase.id);
  };

  return (
    <div className="space-y-8">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-5 rounded-2xl bg-[#0b0e14] border border-white/10 shadow-2xl">
        <div>
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6 text-amber-400" />
            <h2 className="text-xl font-black uppercase tracking-wider text-white">CS2 Case Opening Lab</h2>
          </div>
          <p className="text-xs text-zinc-400 mt-1">Deschide cutii CS2 autentice, colectează skinuri legendare și vinde-le pentru puncte INDUNGI.</p>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={() => setActiveTab("cases")}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all ${activeTab === "cases" ? "bg-[var(--tf-primary)] text-black shadow-lg shadow-orange-500/20" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
          >
            Cutii Disponibile
          </button>
          <button 
            onClick={() => setActiveTab("inventory")}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase transition-all flex items-center gap-2 ${activeTab === "inventory" ? "bg-[var(--tf-primary)] text-black shadow-lg shadow-orange-500/20" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Inventar ({inventory.length})
          </button>
        </div>
      </div>

      {activeTab === "cases" ? (
        <>
          {/* Main Unboxing Spinner Area */}
          {selectedCase && (
            <div className="relative p-6 rounded-3xl bg-[#080a0f] border border-white/10 shadow-2xl overflow-hidden">
              {/* Case Header & Price */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6 relative z-10">
                <div className="flex items-center gap-4">
                  <img src={selectedCase.image} alt={selectedCase.name} className="w-16 h-16 object-contain drop-shadow-[0_0_15px_rgba(255,165,0,0.3)]" />
                  <div>
                    <h3 className="text-lg font-black uppercase text-white tracking-wide">{selectedCase.name}</h3>
                    <p className="text-xs text-zinc-400 max-w-lg">{selectedCase.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-[10px] uppercase font-bold text-zinc-500 block">Preț Deschidere</span>
                    <span className="text-lg font-black text-amber-400">{selectedCase.price} Puncte</span>
                  </div>
                  <button
                    onClick={handleStartOpen}
                    disabled={isSpinning || openCaseMutation.isPending}
                    className={`px-8 py-3.5 rounded-xl font-black uppercase tracking-widest text-sm transition-all duration-300 shadow-xl ${
                      isSpinning 
                        ? "bg-zinc-700 text-zinc-400 cursor-not-allowed" 
                        : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black shadow-orange-500/30 hover:scale-105 active:scale-95"
                    }`}
                  >
                    {isSpinning ? "Se deschide..." : `DESCHIDE CUTIA (${selectedCase.price} PCT)`}
                  </button>
                </div>
              </div>

              {/* ROULETTE REEL VIEWPORT */}
              <div className="relative my-8 py-6 px-2 rounded-2xl bg-black/60 border border-white/10 overflow-hidden shadow-inner">
                {/* Center Winner Pointer */}
                <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-1 bg-amber-400 z-30 shadow-[0_0_15px_#f59e0b]">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[10px] border-t-amber-400" />
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[10px] border-b-amber-400" />
                </div>

                {/* Shading gradients on sides */}
                <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#080a0f] to-transparent z-20 pointer-events-none" />
                <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#080a0f] to-transparent z-20 pointer-events-none" />

                {/* The Reel Cards Track */}
                <div 
                  ref={reelRef}
                  className="flex items-center gap-3 select-none"
                  style={{ width: "max-content" }}
                >
                  {(reel.length > 0 ? reel : selectedCase.skins.concat(selectedCase.skins, selectedCase.skins)).map((skin, i) => {
                    const rarity = RARITY_THEMES[skin.rarity] || RARITY_THEMES["mil-spec"];
                    return (
                      <div 
                        key={i}
                        className="flex flex-col items-center justify-between p-3 rounded-xl border transition-all shrink-0 relative overflow-hidden"
                        style={{ 
                          width: CARD_WIDTH, 
                          height: 190,
                          backgroundColor: rarity.bg,
                          borderColor: rarity.border,
                          boxShadow: isSpinning && i === WINNING_SLOT ? `0 0 25px ${rarity.color}` : 'none'
                        }}
                      >
                        {/* Top weapon indicator */}
                        <span className="text-[10px] font-bold uppercase text-zinc-400 tracking-wider truncate w-full text-center">
                          {skin.weapon}
                        </span>

                        {/* Weapon Image */}
                        <div className="relative w-full h-24 flex items-center justify-center my-1">
                          <img 
                            src={skin.image} 
                            alt={skin.name} 
                            className="max-h-full max-w-full object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]"
                          />
                        </div>

                        {/* Bottom Name & Rarity Bar */}
                        <div className="w-full text-center">
                          <p className="text-xs font-black text-white truncate" title={skin.name}>{skin.name}</p>
                          <span className={`text-[9px] font-bold uppercase tracking-widest ${rarity.text}`}>
                            {rarity.label}
                          </span>
                        </div>

                        {/* Rarity Bottom Stripe */}
                        <div 
                          className="absolute bottom-0 inset-x-0 h-1"
                          style={{ backgroundColor: rarity.color, boxShadow: `0 0 10px ${rarity.color}` }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* WINNING CELEBRATION MODAL */}
              <AnimatePresence>
                {showCelebration && winningSkin && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="p-6 mt-6 rounded-2xl border text-center relative z-20 overflow-hidden"
                    style={{ 
                      backgroundColor: RARITY_THEMES[winningSkin.rarity]?.bg || "rgba(255,255,255,0.05)",
                      borderColor: RARITY_THEMES[winningSkin.rarity]?.border || "#ffaa00"
                    }}
                  >
                    <div className="flex items-center justify-center gap-2 mb-2 text-amber-400 font-bold uppercase text-xs tracking-widest">
                      <Sparkles className="w-4 h-4" /> FELICITĂRI! AI DEBLOCAT UN SKIN NOU <Sparkles className="w-4 h-4" />
                    </div>

                    <h2 className="text-2xl font-black text-white uppercase tracking-wider mb-1">
                      {winningSkin.weapon} | {winningSkin.name}
                    </h2>
                    <span className={`text-xs font-black uppercase tracking-widest ${RARITY_THEMES[winningSkin.rarity]?.text}`}>
                      {RARITY_THEMES[winningSkin.rarity]?.label} • Valoare: {winningSkin.price} Pct
                    </span>

                    <div className="my-4 max-w-xs mx-auto">
                      <img src={winningSkin.image} alt={winningSkin.name} className="w-48 h-32 object-contain mx-auto drop-shadow-[0_0_25px_rgba(255,255,255,0.2)]" />
                    </div>

                    <div className="flex justify-center gap-3">
                      <button 
                        onClick={() => setShowCelebration(false)}
                        className="px-6 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase transition-all"
                      >
                        Păstrează în Inventar
                      </button>
                      <button 
                        onClick={handleStartOpen}
                        className="px-6 py-2.5 rounded-xl bg-[var(--tf-primary)] hover:bg-orange-400 text-black font-black text-xs uppercase tracking-wider transition-all shadow-lg"
                      >
                        Deschide din nou ({selectedCase.price} Pct)
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Case Contents Grid */}
              <div className="mt-8">
                <h4 className="text-xs font-black uppercase tracking-widest text-zinc-400 mb-4">Posibile Obiecte în {selectedCase.name}:</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2.5">
                  {selectedCase.skins.map((skin) => {
                    const rarity = RARITY_THEMES[skin.rarity] || RARITY_THEMES["mil-spec"];
                    return (
                      <div 
                        key={skin.id}
                        className="p-2.5 rounded-xl border bg-black/40 hover:bg-white/5 transition-all text-center flex flex-col items-center justify-between"
                        style={{ borderColor: rarity.border }}
                      >
                        <span className="text-[9px] font-bold text-zinc-400 uppercase truncate w-full">{skin.weapon}</span>
                        <img src={skin.image} alt={skin.name} className="w-16 h-12 object-contain my-1" />
                        <p className="text-[10px] font-bold text-white truncate w-full">{skin.name}</p>
                        <span className={`text-[8px] font-bold uppercase ${rarity.text}`}>{rarity.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Cases Selection Shelf */}
          <div>
            <h3 className="text-sm font-black uppercase tracking-wider text-zinc-400 mb-4">Alege o cutie din magazin:</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {casesList.map((c) => {
                const isSelected = selectedCase?.id === c.id;
                return (
                  <div
                    key={c.id}
                    onClick={() => {
                      if (!isSpinning) {
                        setSelectedCase(c);
                        setShowCelebration(false);
                      }
                    }}
                    className={`p-5 rounded-2xl border transition-all cursor-pointer flex items-center gap-4 ${
                      isSelected 
                        ? "bg-orange-500/10 border-orange-500/60 shadow-xl shadow-orange-500/10 scale-[1.02]" 
                        : "bg-[#0b0e14] border-white/10 hover:border-white/20 hover:bg-white/[0.02]"
                    }`}
                  >
                    <img src={c.image} alt={c.name} className="w-20 h-20 object-contain shrink-0 drop-shadow-md" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-black uppercase text-white truncate">{c.name}</h4>
                      <p className="text-xs text-zinc-400 line-clamp-2 my-1">{c.description}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs font-black text-amber-400">{c.price} Puncte</span>
                        <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-lg ${isSelected ? "bg-orange-500 text-black font-black" : "bg-white/10 text-zinc-300"}`}>
                          {isSelected ? "Selectat" : "Selectează"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        /* USER INVENTORY TAB */
        <div className="p-6 rounded-3xl bg-[#080a0f] border border-white/10 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-black uppercase text-white">Inventarul Tău de Skinuri</h3>
              <p className="text-xs text-zinc-400">Skinurile deblocate pot fi păstrate ca trofeu sau vândute instant pentru puncte de forum.</p>
            </div>
            <span className="text-xs font-mono text-zinc-400 bg-white/5 px-3 py-1.5 rounded-xl border border-white/10">
              Total skinuri: <strong className="text-white">{inventory.length}</strong>
            </span>
          </div>

          {inventory.length === 0 ? (
            <div className="py-16 text-center text-zinc-500">
              <Gift className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-bold">Nu ai niciun skin în inventar momentan.</p>
              <p className="text-xs mt-1">Deschide o cutie CS2 pentru a câștiga primul tău skin!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {inventory.map((item) => {
                const skin = item.skin || {};
                const rarity = RARITY_THEMES[skin.rarity] || RARITY_THEMES["mil-spec"];
                return (
                  <div 
                    key={item.id}
                    className="p-4 rounded-2xl border bg-black/40 hover:bg-white/5 transition-all flex flex-col justify-between"
                    style={{ borderColor: rarity.border }}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-mono text-zinc-500">{item.case_name}</span>
                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${rarity.text}`} style={{ backgroundColor: rarity.bg }}>
                          {rarity.label}
                        </span>
                      </div>

                      <div className="w-full h-32 flex items-center justify-center my-2">
                        <img src={skin.image} alt={skin.name} className="max-h-full max-w-full object-contain drop-shadow-md" />
                      </div>

                      <h4 className="text-sm font-black text-white truncate">{skin.weapon}</h4>
                      <p className="text-xs text-zinc-300 truncate font-semibold">{skin.name}</p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                      <div>
                        <span className="text-[9px] text-zinc-500 uppercase block">Valoare</span>
                        <span className="text-xs font-bold text-amber-400">+{skin.price || 50} Pct</span>
                      </div>

                      <button
                        onClick={() => sellSkinMutation.mutate(item.id)}
                        disabled={sellSkinMutation.isPending}
                        className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 text-[11px] font-bold transition-all"
                      >
                        Vinde Skin
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
